import { useEffect, useRef } from "react";
import { useSelfService, type StepProgress } from "../context";

const NO_TERMINALS: readonly string[] = [];

interface FlowStepProgressResult {
  lastIndex: number;
  progress: StepProgress | null;
}

function resolveFlowStepProgress({
  currentState,
  lastIndex,
  milestones,
  terminalStates,
}: {
  currentState: string;
  lastIndex: number;
  milestones: readonly string[] | null;
  terminalStates: readonly string[];
}): FlowStepProgressResult {
  if (!milestones || milestones.length === 0) {
    return {
      lastIndex: 0,
      progress: null,
    };
  }

  if (terminalStates.includes(currentState)) {
    const terminalIndex = milestones.length - 1;
    return {
      lastIndex: terminalIndex,
      progress: { current: milestones.length, total: milestones.length },
    };
  }

  const currentIndex = milestones.indexOf(currentState);
  const nextIndex = currentIndex >= 0 ? currentIndex : lastIndex;

  return {
    lastIndex: nextIndex,
    progress: {
      current: nextIndex + 1,
      total: milestones.length,
    },
  };
}

/**
 * Publishes the active flow's coarse step progress to the global footer stepper
 * (the handoff BotBar). `milestones` is the ordered list of primary state ids;
 * states not listed (side-paths, errors, transient states) hold the furthest
 * milestone reached so the bar never jumps backward, and `terminalStates` snap
 * the bar to full. Pass `milestones: null` to hide the stepper entirely (e.g.
 * the emergency flow, where step pressure is undesirable).
 *
 * Prefer {@link useFlowPhaseProgress} for flows whose states group into named
 * phases: a bare "Schritt x / y" counter stalls on side paths (KC-02).
 *
 * @since 1.0.0
 * @example
 * ```ts
 * useFlowStepProgress(currentState, ["start", "confirm", "done"]);
 * ```
 */
export function useFlowStepProgress(
  currentState: string,
  milestones: readonly string[] | null,
  terminalStates: readonly string[] = NO_TERMINALS
): void {
  const { setStepProgress } = useSelfService();
  const lastIndexRef = useRef(0);

  useEffect(
    function publishStepProgress() {
      const resolvedProgress = resolveFlowStepProgress({
        currentState,
        lastIndex: lastIndexRef.current,
        milestones,
        terminalStates,
      });
      lastIndexRef.current = resolvedProgress.lastIndex;
      setStepProgress(resolvedProgress.progress);
    },
    [currentState, milestones, terminalStates, setStepProgress]
  );
}

/**
 * One named phase of a flow for {@link useFlowPhaseProgress}.
 *
 * - `label`: translated, short phase name shown in the footer ("Anmelden").
 * - `states`: every state id that belongs to the phase.
 * - `subSteps`: optional ordered state ids inside the phase that form a
 *   sub-flow (e.g. a three-screen registration); the footer then shows
 *   "Teil x von y" for them. Each sub-step id must also be listed in `states`.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const phase: FlowPhase = {
 *   label: t("self-service.check-in.progress.identify"),
 *   states: ["cardCheck", "patientLookup", "registerBasics", "registerDetails"],
 *   subSteps: ["registerBasics", "registerDetails"],
 * };
 * ```
 */
export interface FlowPhase {
  readonly label: string;
  readonly states: readonly string[];
  readonly subSteps?: readonly string[];
}

/**
 * Phase configuration of a flow for {@link useFlowPhaseProgress}.
 *
 * `completeStates` are states in which the flow's work is finished (for
 * check-in: everything after the committed check-in that is not itself a
 * phase); the footer then shows every phase as done and a full bar. States
 * that belong to no phase hold the furthest phase reached, so the bar never
 * moves backward on a side path.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const config: FlowPhaseConfig = {
 *   phases: [{ label: "Anmelden", states: ["cardCheck"] }, { label: "Fertig", states: ["done"] }],
 *   completeStates: ["done"],
 * };
 * ```
 */
export interface FlowPhaseConfig {
  readonly phases: readonly FlowPhase[];
  readonly completeStates?: readonly string[];
}

/**
 * Result of {@link resolveFlowPhaseProgress}: the progress to publish and the
 * phase index to hold for the next unlisted state.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const result: FlowPhaseProgressResult = { phaseIndex: 0, progress: null };
 * ```
 */
export interface FlowPhaseProgressResult {
  readonly phaseIndex: number;
  readonly progress: StepProgress | null;
}

/**
 * Pure resolver behind {@link useFlowPhaseProgress}: maps a state id to the
 * named-phase progress the footer renders. Unlisted states hold
 * `heldPhaseIndex`; a phase is never left for an earlier one by an unlisted
 * state, but an explicitly listed earlier state (the patient pressed "Zurück")
 * does move the bar back, because that is where the patient now is.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * resolveFlowPhaseProgress("registerDetails", config, 0);
 * // { phaseIndex: 0, progress: { current: 1, total: 3, phases, subStep: { current: 2, total: 2 } } }
 * ```
 */
export function resolveFlowPhaseProgress(
  currentState: string,
  config: FlowPhaseConfig,
  heldPhaseIndex: number,
): FlowPhaseProgressResult {
  const phases = config.phases;
  if (phases.length === 0) return { phaseIndex: 0, progress: null };
  const labels = phases.map((phase) => phase.label);
  const total = phases.length;

  if (config.completeStates?.includes(currentState) === true) {
    return {
      phaseIndex: total - 1,
      progress: { current: total, total, phases: labels, complete: true },
    };
  }

  const listedIndex = phases.findIndex((phase) =>
    phase.states.includes(currentState),
  );
  const phaseIndex =
    listedIndex >= 0 ? listedIndex : Math.min(Math.max(heldPhaseIndex, 0), total - 1);
  const phase = phases[phaseIndex];
  const subStepIndex = phase?.subSteps?.indexOf(currentState) ?? -1;
  const subStepTotal = phase?.subSteps?.length ?? 0;

  return {
    phaseIndex,
    progress: {
      current: phaseIndex + 1,
      total,
      phases: labels,
      ...(subStepIndex >= 0 && subStepTotal > 1
        ? { subStep: { current: subStepIndex + 1, total: subStepTotal } }
        : {}),
    },
  };
}

/**
 * Publishes named-phase progress ("Anmelden · Versicherung · Fertig") for the
 * active flow to the footer. Sub-flows inside a phase report "Teil x von y"
 * instead of freezing the counter, and `completeStates` mark the flow as done
 * once its work is committed (KC-02, KO-20). Pass a memoised `config` (its
 * labels are translated strings).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const config = useMemo(() => ({
 *   phases: [
 *     { label: t("self-service.check-in.progress.identify"), states: ["cardCheck", "patientLookup"] },
 *     { label: t("self-service.check-in.progress.insurance"), states: ["eebConsent"] },
 *     { label: t("self-service.check-in.progress.done"), states: [] },
 *   ],
 *   completeStates: ["done"],
 * }), [t]);
 * useFlowPhaseProgress(currentState, config);
 * ```
 */
export function useFlowPhaseProgress(
  currentState: string,
  config: FlowPhaseConfig,
): void {
  const { setStepProgress } = useSelfService();
  const heldPhaseIndexRef = useRef(0);

  useEffect(
    function publishPhaseProgress() {
      const resolved = resolveFlowPhaseProgress(
        currentState,
        config,
        heldPhaseIndexRef.current,
      );
      heldPhaseIndexRef.current = resolved.phaseIndex;
      setStepProgress(resolved.progress);
    },
    [config, currentState, setStepProgress],
  );
}
